import type { AccountOrgValue, ClientConfig, ClientMetadata } from '@/types';

/**
 * The contract a client is on: how Arcanum sells to it.
 *
 * Two records hold it, and the portal is the only writer of either:
 *   - `contract` (and `partner`) on the client's row in `numa-client-metadata`.
 *     It holds all six values. No deploy reads it.
 *   - `contractType` in the client config. It holds only the three values a
 *     deploy accepts (credits, legacy, own-account): how the client is billed.
 *     The portal writes it from the contract on the same save (`billingFor`).
 *
 * The split keeps every deploy working: an image only knows the three billing
 * values, and its config schema refuses any other. Trial, partner and internal
 * live only in the portal's own table.
 */
export const CONTRACT_VALUES = ['credits', 'own-account', 'trial', 'partner', 'internal', 'legacy'] as const;
export type Contract = (typeof CONTRACT_VALUES)[number];

/** How a client is billed: the client config `contractType`, the three values a deploy accepts. */
export type Billing = NonNullable<ClientConfig['contractType']>;
export const BILLING_VALUES: readonly Billing[] = ['credits', 'legacy', 'own-account'];

export const CONTRACT_DISPLAY: Record<Contract, { label: string; hint: string; variant: string }> = {
  credits: {
    label: 'Credits',
    hint: 'The client pays by Numa credits. The default for a NextGen account.',
    variant: 'success',
  },
  'own-account': {
    label: 'Own account',
    hint: "Numa runs in the client's own AWS account and the client pays AWS directly. Its people see real AWS dollars, with no credits.",
    variant: 'dark',
  },
  trial: {
    label: 'Trial',
    hint: 'A trial. Billed as Credits. Set the trial dates below.',
    variant: 'warning',
  },
  partner: {
    label: 'Partner',
    hint: 'The client came through a partner. Choose the partner and how the client is billed.',
    variant: 'info',
  },
  internal: {
    label: 'Internal',
    hint: "One of Arcanum's own stacks: HQ, a dev or demo stack, a quota account. Billed as Credits.",
    variant: 'primary',
  },
  legacy: {
    label: 'Legacy',
    hint: "An older or custom contract. The client's admins cannot choose the model new chats start on.",
    variant: 'secondary',
  },
};

export const BILLING_DISPLAY: Record<Billing, string> = {
  credits: 'Credits',
  legacy: 'Legacy',
  'own-account': 'Own account',
};

export const isContract = (value: unknown): value is Contract =>
  typeof value === 'string' && (CONTRACT_VALUES as readonly string[]).includes(value);

export const isBilling = (value: unknown): value is Billing =>
  typeof value === 'string' && (BILLING_VALUES as readonly string[]).includes(value);

/**
 * The client config `contractType` a contract saves. A partner client is billed
 * as the person chose (`partnerBilling`), Credits when nothing was chosen.
 */
export function billingFor(contract: Contract, partnerBilling?: unknown): Billing {
  switch (contract) {
    case 'credits':
    case 'trial':
    case 'internal':
      return 'credits';
    case 'own-account':
      return 'own-account';
    case 'legacy':
      return 'legacy';
    case 'partner':
      return isBilling(partnerBilling) ? partnerBilling : 'credits';
  }
}

/**
 * The client's contract: the portal's record when it holds one, else the
 * billing value in the config (a record from before contracts were kept),
 * else undefined: nobody has set it.
 */
export function resolveContract(
  metadata: Pick<ClientMetadata, 'contract'> | undefined,
  config: Pick<ClientConfig, 'contractType'> | undefined
): Contract | undefined {
  if (isContract(metadata?.contract)) return metadata.contract;
  if (isBilling(config?.contractType)) return config.contractType;
  return undefined;
}

/**
 * The contract a new client starts on, from the organisation of its AWS
 * account: Credits in NextGen, Own account in a customer's own account,
 * Internal in Arcanum's own. Unknown gives none: the person chooses.
 */
export function defaultContractForOrg(org: AccountOrgValue | null | undefined): Contract | '' {
  if (org === 'nextgen') return 'credits';
  if (org === 'standalone') return 'own-account';
  if (org === 'arcanum') return 'internal';
  return '';
}

/**
 * A client Arcanum can invoice for credits: on the Credits contract, or a
 * partner billed as Credits, and not Inactive. Only these clients' use past
 * their allocation is money owed to Arcanum.
 *
 * Every other client shows its use and nothing owed. A legacy client is on an
 * older contract, an own-account client pays AWS itself, and a trial or an
 * internal stack pays nothing. Their stacks still meter credits and can end a
 * month below zero, so a balance alone never says a client owes money.
 *
 * `billing` is the client config `contractType`. On a partner contract it is
 * the only record of who pays. The rollup holds the same rule in Python
 * (`numa_fleet_analytics_rollup/collectible.py`): change both together.
 */
export function isCollectible(contract: Contract | undefined, billing: unknown, inactive = false): boolean {
  if (inactive) return false;
  if (contract === 'credits') return true;
  return contract === 'partner' && billingFor('partner', billing) === 'credits';
}

/** Today's date in New Zealand, `yyyy-mm-dd`. */
const nzToday = (now: Date): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Pacific/Auckland',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);

/**
 * A trial whose end date has passed. The end date is the trial's last day in
 * New Zealand, so the trial is expired from the next day, as the Client checks
 * page and the trial alert in Slack count it.
 */
export function trialExpired(metadata: Pick<ClientMetadata, 'trialEndDate'> | undefined, now = new Date()): boolean {
  const end = metadata?.trialEndDate?.slice(0, 10);
  return !!end && nzToday(now) > end;
}

/** The badge for a client's contract, or null when none is set. */
export function contractBadge(
  metadata: Pick<ClientMetadata, 'contract' | 'partner' | 'trialEndDate'> | undefined,
  config: Pick<ClientConfig, 'contractType'> | undefined
): { label: string; variant: string } | null {
  const contract = resolveContract(metadata, config);
  if (!contract) return null;
  if (contract === 'trial' && trialExpired(metadata)) return { label: 'Trial, expired', variant: 'danger' };
  if (contract === 'partner' && metadata?.partner) {
    return { label: `Partner: ${metadata.partner}`, variant: CONTRACT_DISPLAY.partner.variant };
  }
  return { label: CONTRACT_DISPLAY[contract].label, variant: CONTRACT_DISPLAY[contract].variant };
}

/** A partner's name as people type it: trimmed, inner spaces collapsed. */
export const normalisePartner = (name: string): string => name.trim().replace(/\s+/g, ' ');

/** The partners named on any client, one name for each partner, sorted. */
export function partnerNames(rows: Iterable<Pick<ClientMetadata, 'partner'>>): string[] {
  const byKey = new Map<string, string>();
  for (const row of rows) {
    const name = row.partner ? normalisePartner(row.partner) : '';
    if (name && !byKey.has(name.toLowerCase())) byKey.set(name.toLowerCase(), name);
  }
  return [...byKey.values()].sort((a, b) => a.localeCompare(b));
}

/**
 * The partner name to save: the spelling already on another client when the
 * typed name matches it in any case, so "ohnyx" joins "Ohnyx" and never makes
 * a second partner.
 */
export function canonicalPartner(typed: string, known: readonly string[]): string {
  const name = normalisePartner(typed);
  return known.find((k) => k.toLowerCase() === name.toLowerCase()) ?? name;
}

/** The contract part of the client form, as the form holds it. An empty string is "none". */
export interface ContractFormState {
  contract: Contract | '';
  partner: string;
  /** `yyyy-mm-dd`, or an empty string for none. */
  trialStartDate: string;
  trialEndDate: string;
}

export const EMPTY_CONTRACT: ContractFormState = { contract: '', partner: '', trialStartDate: '', trialEndDate: '' };

/** What stops the contract from saving, or null when it can save. */
export function contractProblem(state: ContractFormState): string | null {
  if (!state.contract) return 'Choose a contract type.';
  if (state.contract === 'partner' && !normalisePartner(state.partner)) return 'Choose the partner, or type a new one.';
  return null;
}
